import { BookmarkedRoute, Itinerary } from '../types/ferry';
import { RouteResult } from '../components/RouteResults';
import { logger } from '../utils/logger';

const BOOKMARKS_KEY = 'ferry_bookmarks';
const ITINERARIES_KEY = 'ferry_itineraries';

export class BookmarkService {
  private static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Bookmark operations
  static saveBookmark(routeResult: RouteResult, selectedDate: string, customName?: string): BookmarkedRoute {
    const bookmarks = this.getBookmarks();
    
    const bookmark: BookmarkedRoute = {
      id: this.generateId(),
      routeResult,
      selectedDate,
      bookmarkedAt: new Date(),
      customName,
    };

    bookmarks.push(bookmark);
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
    
    return bookmark;
  }

  static getBookmarks(): BookmarkedRoute[] {
    try {
      const stored = localStorage.getItem(BOOKMARKS_KEY);
      if (!stored) return [];
      
      const bookmarks = JSON.parse(stored);
      // Convert date strings back to Date objects
      return bookmarks.map((bookmark: any) => ({
        ...bookmark,
        bookmarkedAt: new Date(bookmark.bookmarkedAt),
      }));
    } catch (error) {
      logger.error('Error loading bookmarks:', error);
      return [];
    }
  }

  static removeBookmark(bookmarkId: string): void {
    const bookmarks = this.getBookmarks();
    const filtered = bookmarks.filter(bookmark => bookmark.id !== bookmarkId);
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(filtered));
  }

  static updateBookmark(bookmarkId: string, updates: Partial<BookmarkedRoute>): void {
    const bookmarks = this.getBookmarks();
    const index = bookmarks.findIndex(bookmark => bookmark.id === bookmarkId);
    
    if (index !== -1) {
      bookmarks[index] = { ...bookmarks[index], ...updates };
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
    }
  }

  static reorderBookmarks(newOrder: BookmarkedRoute[]): void {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(newOrder));
  }

  static isBookmarked(routeResult: RouteResult, selectedDate: string): BookmarkedRoute | null {
    const bookmarks = this.getBookmarks();
    return bookmarks.find(bookmark => 
      bookmark.routeResult.route.id === routeResult.route.id &&
      bookmark.routeResult.service.id === routeResult.service.id &&
      bookmark.routeResult.schedule.departure === routeResult.schedule.departure &&
      bookmark.selectedDate === selectedDate
    ) || null;
  }

  // Itinerary operations
  static createItinerary(name: string, routes: BookmarkedRoute[] = []): Itinerary {
    const itineraries = this.getItineraries();
    
    const itinerary: Itinerary = {
      id: this.generateId(),
      name,
      routes,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    itineraries.push(itinerary);
    localStorage.setItem(ITINERARIES_KEY, JSON.stringify(itineraries));
    
    return itinerary;
  }

  static getItineraries(): Itinerary[] {
    try {
      const stored = localStorage.getItem(ITINERARIES_KEY);
      if (!stored) return [];
      
      const itineraries = JSON.parse(stored);
      // Convert date strings back to Date objects
      return itineraries.map((itinerary: any) => ({
        ...itinerary,
        createdAt: new Date(itinerary.createdAt),
        updatedAt: new Date(itinerary.updatedAt),
        routes: itinerary.routes.map((route: any) => ({
          ...route,
          bookmarkedAt: new Date(route.bookmarkedAt),
        })),
      }));
    } catch (error) {
      logger.error('Error loading itineraries:', error);
      return [];
    }
  }

  static updateItinerary(itineraryId: string, updates: Partial<Itinerary>): void {
    const itineraries = this.getItineraries();
    const index = itineraries.findIndex(itinerary => itinerary.id === itineraryId);
    
    if (index !== -1) {
      itineraries[index] = { 
        ...itineraries[index], 
        ...updates, 
        updatedAt: new Date() 
      };
      localStorage.setItem(ITINERARIES_KEY, JSON.stringify(itineraries));
    }
  }

  static deleteItinerary(itineraryId: string): void {
    const itineraries = this.getItineraries();
    const filtered = itineraries.filter(itinerary => itinerary.id !== itineraryId);
    localStorage.setItem(ITINERARIES_KEY, JSON.stringify(filtered));
  }

  // Utility functions
  static exportBookmarks(): string {
    const data = {
      bookmarks: this.getBookmarks(),
      itineraries: this.getItineraries(),
      exportedAt: new Date().toISOString(),
    };
    return JSON.stringify(data, null, 2);
  }

  static importBookmarks(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      if (data.bookmarks) {
        localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(data.bookmarks));
      }
      if (data.itineraries) {
        localStorage.setItem(ITINERARIES_KEY, JSON.stringify(data.itineraries));
      }
      return true;
    } catch (error) {
      logger.error('Error importing bookmarks:', error);
      return false;
    }
  }

  static clearAllData(): void {
    localStorage.removeItem(BOOKMARKS_KEY);
    localStorage.removeItem(ITINERARIES_KEY);
  }
}